import { getAuth } from 'firebase/auth';
import Logger from '../../utils/Logger';
import { fetchWithFabricRetry } from '../../utils/fabricReEnroll';
import { API_ENDPOINT_MARKETPLACE_USER_LISTINGS } from '../Constants';
import BaseMarketplaceService from './BaseMarketplaceService';

export default class ListingsService extends BaseMarketplaceService {
  async getLatestIPFSMappings() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseUrl}/nft/latest`, { headers });
      if (!response.ok) throw new Error('Failed to fetch latest NFTs');
      const data = await response.json();
      return data.nfts || [];
    } catch (error) {
      console.error('Error fetching latest NFTs:', error);
      throw error;
    }
  }

  async getListings({
    page = 1,
    limit = 10,
    sortBy = 'price',
    sortOrder = 'asc',
    minPrice,
    maxPrice,
    category,
    status,
    isPublic = false,
    skipSort = false,
  } = {}) {
    try {
      let headers;
      if (isPublic) {
        headers = { 'Content-Type': 'application/json' };
      } else {
        try {
          headers = await this.getAuthHeaders();
        } catch (authError) {
          console.warn('Auth failed, falling back to public access:', authError.message);
          headers = { 'Content-Type': 'application/json' };
        }
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (!skipSort && sortBy) {
        params.append('sortBy', sortBy);
        params.append('sortOrder', sortOrder);
      }
      if (minPrice) params.append('minPrice', minPrice.toString());
      if (maxPrice) params.append('maxPrice', maxPrice.toString());
      if (category) params.append('category', category);
      if (status) params.append('status', status);

      const response = await fetch(`${this.baseUrl}/nft/marketplace/listings?${params.toString()}`, { headers });

      if (!response.ok) {
        const errorText = await response.text();
        if ((errorText.includes('no_usable_index') || errorText.includes('No index exists')) && !skipSort) {
          return this.getListings({
            page,
            limit,
            minPrice,
            maxPrice,
            category,
            status,
            isPublic,
            skipSort: true,
          });
        }
        throw new Error(errorText);
      }

      const data = await response.json();
      return {
        listings: data.listings || [],
        total: data.total || 0,
        totalPages: data.totalPages || Math.ceil((data.total || 0) / limit),
        currentPage: page,
        limit,
      };
    } catch (error) {
      console.error('Error fetching listings:', error);
      throw error;
    }
  }

  async buyNFT(listingId) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/nft/marketplace/buy`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ listingId }),
    });
    if (!response.ok) throw new Error(await response.text());
    return response.json();
  }

  async cancelListing(listingId) {
    if (!listingId) throw new Error('Listing ID is required');
    const headers = await this.getAuthHeaders();
    const response = await fetchWithFabricRetry(`${this.baseUrl}/nft/marketplace/delist/${listingId}`, {
      method: 'POST',
      headers,
    });
    if (!response.ok) {
      const errorText = await response.text();
      Logger.error('Delist error:', errorText);
      throw new Error(errorText);
    }
    return response.json();
  }

  async updateListingPrice(listingId, newPrice) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/nft/marketplace/update-price`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ listingId, price: newPrice }),
    });
    if (!response.ok) throw new Error(await response.text());
    return response.json();
  }

  async getListingHistory(listingId) {
    if (!listingId) throw new Error('Listing ID is required');
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseUrl}/nft/marketplace/history/${listingId}`, {
        method: 'GET',
        headers,
      });
      if (!response.ok) {
        if (response.status === 404) return { history: [] };
        throw new Error(`Failed to fetch history: ${response.status}`);
      }
      const data = await response.json();
      const history = Array.isArray(data) ? data : data.history || [];
      return {
        history: history.sort((a, b) => new Date(b.timestamp || b.date || 0) - new Date(a.timestamp || a.date || 0)),
      };
    } catch (error) {
      console.error('Error fetching listing history:', error);
      throw error;
    }
  }

  async getUserListings() {
    const headers = await this.getAuthHeaders();
    try {
      const uid = getAuth().currentUser?.uid;
      const url = typeof API_ENDPOINT_MARKETPLACE_USER_LISTINGS === 'function'
        ? API_ENDPOINT_MARKETPLACE_USER_LISTINGS(uid)
        : API_ENDPOINT_MARKETPLACE_USER_LISTINGS;
      const response = await fetch(url, { method: 'GET', headers });
      if (!response.ok) throw new Error(`Failed to fetch user listings: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching user listings:', error);
      throw error;
    }
  }

  formatListingData(formData) {
    return {
      nftId: formData.nftId,
      price: Number(formData.price),
      category: formData.category || '',
      tags: formData.tags
        ? (Array.isArray(formData.tags) ? formData.tags : formData.tags.split(',').map((tag) => tag.trim()))
        : [],
      metadata: {
        description: formData.description || '',
        attributes: {
          rarity: formData.rarity || 'common',
          edition: formData.edition || '',
          ...formData.attributes,
        },
      },
    };
  }

  async listNFTForSale(nftId, price) {
    if (!nftId) throw new Error('NFT ID is required');
    const numPrice = Number(price);
    if (price === null || price === undefined || Number.isNaN(numPrice) || numPrice < 0) {
      throw new Error('Valid price is required (must be >= 0)');
    }

    try {
      const headers = await this.getAuthHeaders();
      const response = await fetchWithFabricRetry(`${this.baseUrl}/nft/marketplace/list`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ nftId, price: Number(price) }),
      });
      const responseText = await response.text();
      if (!response.ok) throw new Error(responseText || 'Failed to list NFT');
      return responseText ? JSON.parse(responseText) : {};
    } catch (error) {
      Logger.error('Error in listNFTForSale:', error);
      throw error;
    }
  }

  async getNFTById(nftId) {
    if (!nftId) return null;
    try {
      let headers;
      let isAuthenticated = false;
      try {
        headers = await this.getAuthHeaders();
        isAuthenticated = true;
      } catch {
        headers = { 'Content-Type': 'application/json' };
      }

      try {
        const response = await fetch(`${this.baseUrl}/nft/${nftId}`, { method: 'GET', headers });
        if (response.ok) {
          const text = await response.text();
          if (text && text.trim() !== '') {
            try {
              return JSON.parse(text);
            } catch (_) {
              // Continue to fallback
            }
          }
        }
      } catch (_) {
        // Continue to fallback
      }

      try {
        const queryResponse = await fetch(`${this.baseUrl}/nft/query?nftId=${encodeURIComponent(nftId)}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        if (queryResponse.ok) {
          const responseText = await queryResponse.text();
          if (responseText && responseText.trim() !== '') {
            const queryData = JSON.parse(responseText);
            const nfts = Array.isArray(queryData) ? queryData : (queryData.nfts || queryData.data || []);
            if (nfts.length > 0) return nfts.find((n) => n.nftId === nftId) || nfts[0];
          }
        }
      } catch (_) {
        // Continue to fallback
      }

      if (isAuthenticated) {
        try {
          const auth = getAuth();
          const userId = auth.currentUser?.uid;
          if (userId) {
            const ownerHeaders = await this.getAuthHeaders();
            const ownerResponse = await fetch(`${this.baseUrl}/nft/owner/${userId}`, {
              method: 'GET',
              headers: ownerHeaders,
            });
            if (ownerResponse.ok) {
              const responseText = await ownerResponse.text();
              if (responseText && responseText.trim() !== '') {
                const ownerData = JSON.parse(responseText);
                const ownerNFTs = Array.isArray(ownerData) ? ownerData : (ownerData.nfts || ownerData.data || []);
                const nft = ownerNFTs.find((n) => n.nftId === nftId);
                if (nft) return nft;
              }
            }
          }
        } catch (_) {
          // Ignore and return null
        }
      }

      return null;
    } catch (error) {
      Logger.error('Error fetching NFT by ID:', error);
      return null;
    }
  }

  async getListingById(listingId) {
    try {
      if (!listingId) throw new Error('Listing ID is required');
      let headers;
      try {
        headers = await this.getAuthHeaders();
      } catch (authError) {
        Logger.error('getListingById: Failed to get auth headers', authError);
        throw new Error('Authentication failed. Please log in again.');
      }

      const url = `${this.baseUrl}/nft/listing/${listingId}`;
      const response = await fetch(url, { method: 'GET', headers });
      if (!response.ok) {
        if (response.status === 404) {
          try {
            return await this.getNFTById(listingId);
          } catch (_) {
            return null;
          }
        }

        if (response.status === 401) {
          const errorText = await response.text().catch(() => 'Unauthorized');
          let isListingNotFound = false;
          try {
            const errorJson = JSON.parse(errorText);
            if (errorJson.message && errorJson.message.toLowerCase().includes('listing does not exist')) {
              isListingNotFound = true;
            }
          } catch (_) {
            // ignore parse errors
          }

          if (isListingNotFound) {
            try {
              return await this.getNFTById(listingId);
            } catch (_) {
              return null;
            }
          }

          try {
            const nftData = await this.getNFTById(listingId);
            if (nftData) return nftData;
          } catch (_) {
            // continue
          }

          try {
            const auth = getAuth();
            if (auth.currentUser) {
              headers = await this.getAuthHeaders({ forceRefresh: true });
              const retryResponse = await fetch(url, { method: 'GET', headers });
              if (retryResponse.ok) {
                const retryText = await retryResponse.text();
                if (!retryText || retryText.trim() === '') return null;
                try {
                  return JSON.parse(retryText);
                } catch (_) {
                  return null;
                }
              }
            }
          } catch (retryError) {
            Logger.error('getListingById: Retry after token refresh failed', retryError);
          }
          throw new Error('Unauthorized: Please ensure you are logged in. If the problem persists, try refreshing the page.');
        }

        const errorText = await response.text().catch(() => `Status ${response.status}`);
        throw new Error(`Failed to fetch listing: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      Logger.error('Error fetching listing by ID:', error);
      if (error.message && error.message.includes('Unauthorized')) throw error;
      throw new Error(`Failed to fetch listing: ${error.message || 'Unknown error'}`);
    }
  }
}
